import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import React, { useMemo, useState } from "react";
import { brand } from "../../../shared/theme/app-theme";
import ReportStatusIndicator from "../../report/report-status/report-status-indicator";
import { UniversityStatusRow } from "../model";

type SubmittedFilter = "all" | "submitted" | "missing";
type ProgramFilter = "all" | "324" | "204";

type Props = {
  rows: UniversityStatusRow[];
};

const UniversityStatusTable: React.FC<Props> = ({ rows }) => {
  const [search, setSearch] = useState("");
  const [submitted, setSubmitted] = useState<SubmittedFilter>("all");
  const [program, setProgram] = useState<ProgramFilter>("all");

  const submittedCount = useMemo(
    () => rows.filter((row) => row.total > 0).length,
    [rows]
  );
  const missingCount = rows.length - submittedCount;
  const has324Count = useMemo(
    () => rows.filter((row) => row.has324).length,
    [rows]
  );
  const has204Count = useMemo(
    () => rows.filter((row) => row.total > 0).length,
    [rows]
  );

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (submitted === "submitted" && row.total === 0) return false;
      if (submitted === "missing" && row.total > 0) return false;
      if (program === "324" && !row.has324) return false;
      if (program === "204" && row.total === 0) return false;
      if (query && !row.universityName.toLowerCase().includes(query)) {
        return false;
      }
      return true;
    });
  }, [rows, search, submitted, program]);

  const columns = useMemo<GridColDef<UniversityStatusRow>[]>(
    () => [
      {
        field: "universityName",
        headerName: "ВУЗ",
        flex: 1,
        minWidth: 280,
      },
      {
        field: "status",
        headerName: "Статус",
        flex: 1,
        minWidth: 220,
        sortable: false,
        renderCell: (params) => {
          if (params.row.total === 0) {
            return (
              <Typography sx={{ fontSize: 13, color: brand.muted }}>
                Не сдал
              </Typography>
            );
          }
          return (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 1,
                minWidth: 0,
              }}
            >
              {params.row.statuses.map((status) => (
                <ReportStatusIndicator key={status} status={status} />
              ))}
            </Box>
          );
        },
      },
      {
        field: "programs",
        headerName: "Программы",
        width: 280,
        sortable: false,
        renderCell: (params) => {
          if (params.row.total === 0) {
            return (
              <Typography sx={{ fontSize: 13, color: brand.muted }}>—</Typography>
            );
          }
          return (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              <Chip
                size="small"
                label="324"
                title="324-006"
                variant={params.row.has324 ? "filled" : "outlined"}
                sx={{
                  height: 22,
                  fontSize: 12,
                  bgcolor: params.row.has324 ? "#e8f3ee" : "transparent",
                  color: params.row.has324 ? "#1f7a4d" : brand.muted,
                  borderColor: params.row.has324 ? "#1f7a4d" : brand.line,
                }}
              />
              <Chip
                size="small"
                label="204"
                title="204-100, 204-118"
                variant="filled"
                sx={{
                  height: 22,
                  fontSize: 12,
                  bgcolor: "#e8eef8",
                  color: brand.navy,
                  borderColor: brand.navy,
                }}
              />
            </Box>
          );
        },
      },
      {
        field: "students",
        headerName: "Студентов",
        width: 120,
        renderCell: (params) => (
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
            {params.row.total === 0 ? "—" : params.row.students}
          </Typography>
        ),
      },
    ],
    []
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <TextField
          size="small"
          label="Поиск вуза"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: "1 1 240px", minWidth: 200, maxWidth: 360 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="uni-submitted-label">Сдача</InputLabel>
          <Select
            labelId="uni-submitted-label"
            label="Сдача"
            value={submitted}
            onChange={(e) => setSubmitted(e.target.value as SubmittedFilter)}
          >
            <MenuItem value="all">Все ({rows.length})</MenuItem>
            <MenuItem value="submitted">Сдал ({submittedCount})</MenuItem>
            <MenuItem value="missing">Не сдал ({missingCount})</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="uni-program-label">Программа</InputLabel>
          <Select
            labelId="uni-program-label"
            label="Программа"
            value={program}
            onChange={(e) => setProgram(e.target.value as ProgramFilter)}
          >
            <MenuItem value="all">Все</MenuItem>
            <MenuItem value="324">324 ({has324Count})</MenuItem>
            <MenuItem value="204">204 ({has204Count})</MenuItem>
          </Select>
        </FormControl>
        <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
          Показано {filteredRows.length}
        </Typography>
      </Box>
      <DataGrid
        autoHeight
        density="compact"
        rows={filteredRows}
        columns={columns}
        getRowId={(row) => row.universityName}
        disableRowSelectionOnClick
        disableColumnMenu
        pageSizeOptions={[10, 20, 25, 50, 100]}
        initialState={{
          pagination: { paginationModel: { pageSize: 20, page: 0 } },
          sorting: { sortModel: [{ field: "universityName", sort: "asc" }] },
        }}
        sx={{
          "& .MuiDataGrid-cell": {
            display: "flex",
            alignItems: "center",
            py: 0,
          },
          "& .MuiDataGrid-columnHeader": { py: 0 },
          "& .MuiDataGrid-footerContainer": { minHeight: 40 },
        }}
      />
    </Box>
  );
};

export default UniversityStatusTable;
